/**
 * The M8 demand-sink tuning (`src/data/strategic/sinks.json`, plan 2026-10-04 §4.5 SNK-1, D-TBD T24): repairs paid in
 * hull plating, munitions paid in weapon parts, and faction works. Every block ships off (`enabled: false`) until
 * SNK-7 turns it on at gate A. No block names a ware: the sinks draw plating, weapon parts and the works inputs,
 * never tetherstone (plan §2), and `drawStock` refuses tetherstone outright.
 */
import { z } from 'zod'
import { WARE_IDS } from '../wares'

const positive = z.number().positive()
const nonNegative = z.number().nonnegative()
const unit = z.number().min(0).max(1)
const posInt = z.number().int().positive()
const nonNegInt = z.number().int().nonnegative()
const comment = z.string().optional()

/**
 * The works kinds a faction may open (SNK-6t). `yardLevelUp` is listed only if SNK-0 calls for it. `refit` (SNK-8,
 * D-TBD gate A) is the recurring kind: it re-fits one of the faction's own stations, adds nothing and has its own caps
 * and trigger (`works.refit`). LV WK-1b (D5): `megaproject` (index 4) builds one stage of a non-productive structure at
 * the faction's anchor station (`works.megaproject`). Append-only: a kind's index is its candidate `aux` and its
 * `worksOpened` event aux.
 */
export const WORKS_KINDS = ['habitatLevelUp', 'defensePlatform', 'yardLevelUp', 'refit', 'megaproject'] as const
export type WorksKind = (typeof WORKS_KINDS)[number]

/**
 * The saved per-faction history of works open days (`FactionAiStore.worksOpenDays`, SNK-6t): this many slots per
 * faction, so `works.maxPer100d` can be at most this. A save layout constant, not a tuning value.
 */
export const WORKS_OPEN_RING = 8

/**
 * The saved per-faction history of refit open days (`FactionAiStore.refitOpenDays`, SNK-8): this many slots per
 * faction, so `works.refit.maxPer100d` can be at most this. A save layout constant, not a tuning value.
 */
export const REFIT_OPEN_RING = 16

/**
 * SNK-8's refit triggers (`works.refit.trigger`), selectable so SNK-8b can pick by probe: `fabFill` (i) the mean
 * seller fill of the faction's own `fabTemplates` stations (fab and refinery output, not the exchange-diluted own-seller
 * mean) at or above `fabFill`; `agentCap` (ii) live agents + open ship orders at or above `agentShare` × strategic.json
 * `caps.maxAgents`; `either` (i) or (ii); `both` (i) and (ii).
 */
export const REFIT_TRIGGERS = ['fabFill', 'agentCap', 'either', 'both'] as const
export type RefitTrigger = (typeof REFIT_TRIGGERS)[number]

/**
 * LV WK-B3 (bug B3, plan 2026-10-13 D2): what the works surplus trigger reads (`works.trigger`, absent = `ownSellers`).
 * `ownSellers`: the mean seller fill over every own station that sells a trigger ware (exchanges included, as shipped);
 * `fabTemplates`: the mean over only the faction's own `works.triggerTemplates` stations (absent =
 * `works.refit.fabTemplates`: the fab and refinery output lines). The reserve check reads the all-stations mean under both.
 */
export const WORKS_TRIGGERS = ['ownSellers', 'fabTemplates'] as const
export type WorksTrigger = (typeof WORKS_TRIGGERS)[number]

/** The window `works.maxPer100d` counts opens over, in faction days (the key's name). */
export const WORKS_CAP_WINDOW_DAYS = 100

/**
 * SNK-1c (report only): why a faction day's works trigger offered nothing, by the first gate that bound in
 * `worksCandidates` order, or `offered` (a candidate went to the AI's pick). Counted per faction day in the
 * runtime-only `SinkLedger.worksGate` while sinks.json `works.enabled` is on. `maxActiveSites` is ai.json
 * `build.maxActiveSites`; `noTarget` (no listed kind had a target: hab levels used up, no held system with room, the
 * growth cap) is the day `worksBlocked` counts; `treasury` and `reserveFill` are the last refused kind's cap.
 * Append-only: LV WK-1a's `budget` (the tonnage budget `works.budget` refused the kind's bill; booked only under that
 * key) comes after `offered`, so every older index keeps its meaning and the recorder prints it by name.
 */
export const WORKS_GATE = {
  noSurplus: 0,
  cooldown: 1,
  maxActive: 2,
  maxPer100d: 3,
  maxActiveSites: 4,
  noTarget: 5,
  treasury: 6,
  reserveFill: 7,
  offered: 8,
  budget: 9,
} as const
export const WORKS_GATE_NAMES = [
  'noSurplus',
  'cooldown',
  'maxActive',
  'maxPer100d',
  'maxActiveSites',
  'noTarget',
  'treasury',
  'reserveFill',
  'offered',
  'budget',
] as const satisfies readonly (keyof typeof WORKS_GATE)[]
export const WORKS_GATE_COUNT = WORKS_GATE_NAMES.length

export const repairsSinkSchema = z.strictObject({
  enabled: z.boolean(),
  /** Plating t per repair = f × (1 − hp) × the hull's `buildCost.hull_plating`. */
  f: positive,
  /** hp at or above this repairs free, as today. */
  freeAbove: unit,
  /** A draw never takes the station below this fill. */
  reserveFill: unit,
  patrols: z.boolean(),
  patrolRepairBelow: unit,
})

export const munitionsSinkSchema = z.strictObject({
  enabled: z.boolean(),
  tPerStrengthRound: positive,
  emptyStrengthMult: unit,
  /** The magazine holds magShare × the hull's `buildCost.weapon_parts` t. */
  magShare: unit,
  patrols: z.boolean(),
  patrolRefillBelow: unit,
  /** Weapon parts t per player torpedo. */
  torpedoT: positive,
  reserveFill: unit,
})

/**
 * The recurring `refit` works kind (SNK-8, D-TBD gate A; all values placeholders [P] for SNK-8b to size). Its own caps
 * beside the shared ones (D-488's `works.maxPer100d` governs only the other kinds): `maxPer100d` opens per 100 faction
 * days and `maxActive` live refit sites per faction; a station is refitted again only `cooldownDays` after its last
 * refit completed. `bill` is the components + metals a refit needs (whole station, no growth-cap or footprint check);
 * `buildDays` once supplied. `score` is the candidate's AI score. SNK-8c: a supplying refit site draws its bill at market
 * with no ship (`sinks/draw.ts drawRefitSites`), at most `drawPerDay` t per faction day, shared over the bill.
 */
export const refitWorksSchema = z.strictObject({
  maxPer100d: nonNegInt.max(REFIT_OPEN_RING),
  maxActive: nonNegInt,
  cooldownDays: nonNegInt,
  buildDays: posInt,
  drawPerDay: positive,
  score: unit,
  trigger: z.enum(REFIT_TRIGGERS),
  fabFill: unit,
  fabTemplates: z.array(z.string()).min(1),
  agentShare: unit,
  /** Never tetherstone (plan §2): a works input is never the carry. */
  bill: z.partialRecord(z.enum(WARE_IDS), positive).refine((b) => b.tetherstone === undefined && Object.keys(b).length > 0, {
    message: 'works.refit.bill must name at least one ware and never tetherstone',
  }),
})

/** A works bill (t per ware): at least one ware, never tetherstone (plan §2: a works input is never the carry). */
const worksBill = z
  .partialRecord(z.enum(WARE_IDS), positive)
  .refine((b) => b.tetherstone === undefined && Object.keys(b).length > 0, { message: 'a works bill must name at least one ware and never tetherstone' })

/**
 * LV WK-1b (plan 2026-10-13 D5): the `megaproject` works kind, a staged structure at the faction's anchor station (its
 * highest-level station of `templates`, lowest index on ties) that adds no line, level, Security or agents. Stage
 * `n` (1-based) needs `bills[n − 1]` delivered by constructors like any works site, then builds `buildDays`;
 * completion only stamps `charterStations.megaStage` (at most `stages`, a saved Uint8). Required while
 * `megaproject` is in `works.kinds`; absent = not built. All values placeholders [P] (stage bills sized at LD from the
 * measured `S_f`).
 */
export const megaprojectWorksSchema = z
  .strictObject({
    stages: posInt.max(255),
    bills: z.array(worksBill).min(1),
    buildDays: posInt,
    templates: z.array(z.string()).min(1),
    /**
     * D-1121 card A2 (blind §3.2 step 6, internal §1.4): a station of the faction's with 1 ≤ `megaStage` < `stages`
     * stays the target even when another station becomes the anchor, so drift leaves no half-built orphan. Absent =
     * false = today (the anchor is recomputed each time).
     */
    stickyAnchor: z.boolean().optional(),
    /**
     * D-1127 (D-1126, plan 2026-10-20-authority-mega-trigger): the fund trigger, a second trigger for the megaproject
     * kind only, for the listed `factions` (factions.json ids, non-outlaw; the Authority, which owns no fab or refinery
     * at seed). On a faction day the surplus path offered nothing, a listed faction whose treasury sits at or above
     * `treasuryMult` × its start treasury offers its next stage (`megaprojectTarget`, no kind draw) at `score`, within
     * the shared caps (cooldown, `maxActivePerFaction`, `maxPer100d`, ai.json `build.maxActiveSites`), the reserve
     * check at `reserveFill` (absent = `works.reserveFill`), `works.treasuryMult` and a credit budget: the open works'
     * remaining bill at base prices plus this stage's ≤ treasury − the floor (it spends only the fund above the
     * floor; the tonnage `budget` is not read). Absent = off = today. Placeholders [P] for the gate read.
     */
    fundTrigger: z
      .strictObject({
        factions: z
          .array(z.string())
          .min(1)
          .refine((f) => new Set(f).size === f.length, { message: 'expected unique works.megaproject.fundTrigger.factions' }),
        treasuryMult: positive,
        score: unit,
        reserveFill: unit.optional(),
      })
      .optional(),
  })
  .refine((m) => m.bills.length === m.stages, { message: 'works.megaproject.bills needs one bill per stage' })

export const worksSinkSchema = z
  .strictObject({
    enabled: z.boolean(),
    /** Component wares (plating, drive, weapon parts, electronics): the streak counts days at or above this. */
    surplusFill: unit,
    /** Steel and ti_alloy (refined; normally 0.92–0.97 full): the streak counts days at or above this (D-488). */
    surplusFillRefined: unit,
    surplusDays: posInt,
    stopFill: unit,
    reserveFill: unit,
    maxActivePerFaction: nonNegInt,
    maxPer100d: nonNegInt.max(WORKS_OPEN_RING),
    cooldownDays: nonNegInt,
    treasuryMult: nonNegative,
    premiumStart: positive,
    premiumPerDay: nonNegative,
    premiumMax: positive,
    kinds: z.array(z.enum(WORKS_KINDS)).refine((k) => new Set(k).size === k.length, { message: 'expected unique works kinds' }),
    refit: refitWorksSchema,
    /** LV WK-B3: what the surplus trigger reads (`WORKS_TRIGGERS`); absent = `ownSellers` (today's rule). */
    trigger: z.enum(WORKS_TRIGGERS).optional(),
    /** LV WK-B3: the templates `fabTemplates` reads and `worksSurplusT` sums; absent = `works.refit.fabTemplates`. */
    triggerTemplates: z.array(z.string()).min(1).optional(),
    /**
     * LV WK-1a (D3, review M3): the tonnage budget, absent = no budget (today). A works opens only if the bill tonnes
     * still to deliver at `f`'s open works sites plus this bill's tonnes ≤ `surplusShare` × `worksSurplusT` (the
     * measured surplus `S_f`) and ≤ `maxOpenT` when given. Checked last (after the reserve and treasury tests); a
     * refusal books `WORKS_GATE.budget` and never falls back to another kind.
     */
    budget: z.strictObject({ surplusShare: positive, maxOpenT: positive.optional() }).optional(),
    /**
     * LV WK-1a (D4): with this key one listed kind with a target is drawn by weight each faction day
     * (`deriveSeed(seed, 'works', f, day)`) and only it is offered; a kind with no target drops out and the draw
     * renormalises; a kind missing from the record weighs 0. Absent = today (level-ups first, platforms only when no
     * level-up has a target). `refit` has its own action and caps, so it can't be weighted.
     */
    kindWeights: z
      .partialRecord(z.enum(WORKS_KINDS), nonNegative)
      .refine((r) => r.refit === undefined, { message: 'works.kindWeights cannot weight refit (its own action and caps)' })
      .refine((r) => Object.values(r).some((v) => v !== undefined && v > 0), { message: 'works.kindWeights needs a positive weight' })
      .optional(),
    /** LV WK-1b (D5): the megaproject kind's stages and bills (`megaprojectWorksSchema`); absent = not built. */
    megaproject: megaprojectWorksSchema.optional(),
    /**
     * O2 (D-1092): the reach-checked wares. A level-up or platform works bill that needs one opens only where a
     * constructor could source it: a station within `roles.supplier.jumpRange` of the site's system holding a
     * constructor-sized lot (`worksReachOk` in factions/actions, the constructor's own source rule). The platform
     * tries the next held system, a level-up the next station. Megaproject and refit bills are not checked.
     * Absent = no check (today).
     */
    reachWares: z
      .array(z.enum(WARE_IDS))
      .min(1)
      .refine((w) => new Set(w).size === w.length, { message: 'expected unique works.reachWares' })
      .optional(),
  })
  .refine((w) => w.stopFill < w.surplusFill, { message: 'works.stopFill must be below works.surplusFill (hysteresis)' })
  .refine((w) => w.premiumStart <= w.premiumMax, { message: 'works.premiumStart must not exceed works.premiumMax' })
  .refine((w) => !w.kinds.includes('megaproject') || w.megaproject !== undefined, { message: 'works.kinds lists megaproject without a works.megaproject block' })

export const sinksConfigSchema = z.strictObject({
  comment,
  repairs: repairsSinkSchema,
  munitions: munitionsSinkSchema,
  works: worksSinkSchema,
})

export type SinksConfig = z.infer<typeof sinksConfigSchema>
